import type {
  AdminRateCard,
  AdminRateCardResponse,
  ReplaceAdminRateCardRequest,
} from '@mds/shared/types/rate-card';

import { api } from '@/lib/api';

/** The rate card — see `backend/docs/rate-card.md`. */

export const getRateCard = (): Promise<AdminRateCard> =>
  api.get<AdminRateCardResponse>('admin/rate-card').then((r) => r.data);

/**
 * Publishes a replacement version of the whole card.
 *
 * All three lists go at once and an omitted item is deliberately dropped, so
 * this is never called with a partial card.
 */
export const replaceRateCard = (body: ReplaceAdminRateCardRequest): Promise<AdminRateCard> =>
  api.put<AdminRateCardResponse>('admin/rate-card', body).then((r) => r.data);

/** One resource the API says a replacement would strand. */
export interface StrandedResource {
  resourceId: string;
  resourceSlug: string;
  rateItemId: string;
  expectedUnit: string;
  issue: 'missing_rate' | 'incompatible_unit';
}

/** Pulls the stranded resources out of a 400, if that is what the failure was. */
export function strandedResources(details: unknown): StrandedResource[] {
  if (!details || typeof details !== 'object') return [];
  const resources = (details as { resources?: unknown }).resources;
  return Array.isArray(resources) ? (resources as StrandedResource[]) : [];
}

/** Pulls the two version numbers out of a 409, if that is what the failure was. */
export function versionConflict(
  details: unknown,
): { expectedVersionNumber: number; currentVersionNumber: number } | null {
  if (!details || typeof details !== 'object') return null;
  const { expectedVersionNumber, currentVersionNumber } = details as Record<string, unknown>;
  return typeof expectedVersionNumber === 'number' && typeof currentVersionNumber === 'number'
    ? { expectedVersionNumber, currentVersionNumber }
    : null;
}
